import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Form } from 'antd';
import { ExtraTabs, ExtraTabsProps } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import ExecuteContextsFieldV2 from '../Scenario/PolicyForm/ExecuteContextsFieldV2';
import { MsgType, mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import { DescendantExecuteContext } from '../Scenario/PolicyForm/share';
import { useMemo } from 'react';
import { noop } from 'lodash-es';

interface OutStockTaskEmptyProps {
  isTradeIds?: boolean;
  scenarioType: Marketing.StockoutScenarioType;
  msgTypes: MsgType[];
  platform: General.PlatformUpperCase;
  recommend?: Marketing.RecommendTemplate[];
  concepts?: ExtraTabsProps['concepts'];
}

const langRecommendTemplateMap = {
  'zh-CN':
    '#mustache#{{#text}}亲，很抱歉，你的订单 {{/text}}{{#concept}}订单号{{/concept}}{{#text}} 中的 {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} 缺货了，建议您改拍 {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}} ，这个款式也很受欢迎。 \n您有任何疑问或担忧，都可以与我们联系哦 :){{/text}}',
  'en-US':
    '#mustache#{{#text}}Dear customer, the product {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} in your order {{/text}}{{#concept}}订单号{{/concept}}{{#text}} is out of stock. We suggest you to order {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}}. This one is the best seller :) Flash sale! \n If you have any questions, please contact us:){{/text}}',
  id: '#mustache#{{#text}}Kak, Produk {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} pada pesanan Kakak dengan nomor {{/text}}{{#concept}}订单号{{/concept}}{{#text}} saat ini kosong, Kakak bisa coba ganti ke {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}}, tipe ini juga sangat disukai Customer nih  :) Flash sale! \n Jika ada pertanyaan lainnya, bisa segera hubungi kami Kak :){{/text}}',
  th: '#mustache#{{#text}}คุณลูกค้าคะ {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} ในคำสั่งซื้อ {{/text}}{{#concept}}订单号{{/concept}}{{#text}} ของคุณสินค้าหมดค่ะ ขอแนะนำให้คุณเปลี่ยนเป็น {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}} แทนค่ะ รุ่นนี้ก็เป็นที่นิยมมากเช่นกันค่ะ  :) Flash sale! \nหากคุณมีคำถามหรือข้อกังวลอะไร ติดต่อทางเราได้ตลอดเลยค่ะ :){{/text}}',
  ph: '#mustache#{{#text}}Dear customer, ang product {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} na nasa iyong order {{/text}}{{#concept}}订单号{{/concept}}{{#text}} ay out of stock. Ang suggestion namin ay mag-order ng {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}}. Sikat din ang item na ito  :) Flash sale!  \n Kung mayroon kang mga tanong, maaari mo kaming kontakin:){{/text}}',
  vi: '#mustache#{{#text}}Xin chào, sản phẩm {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} trong đơn hàng  {{/text}}{{#concept}}订单号{{/concept}}{{#text}} của bạn đã hết hàng, chúng tôi kiến nghị bạn nên đặt sản phẩm  {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}} ，mẫu này cũng rất được yêu thích đó ạ  :) Flash sale! \n nếu bạn có bất kì vẫn đề gì hãy liên hệ shop nhé ạ :){{/text}}',
  es: '#mustache#{{#text}}Querido, o produto {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} no seu pedido de {{/text}}{{#concept}}订单号{{/concept}}{{#text}} está fora de estoque, recomenda-se que você faça uma troca para {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}}, esse estilo também é muito popular  :) Flash sale! \n Se você tiver alguma dúvida, entre em contato conosco:){{/text}}',
  pt: '#mustache#{{#text}}Estimado, en su pedido {{/text}}{{#concept}}订单号{{/concept}}{{#text}}  se ha agostado {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} de existencia , le sugiero que ordene {{/text}}{{#concept}}推荐商品说明{{/concept}}{{#text}}, este estilo también es muy popular  :) Flash sale!  \nSi tiene alguna pregunta o preocupación, puede ponerse en contacto con nosotros:){{/text}}',
};

const holderData: DescendantExecuteContext = {
  content: mustache2SlateValue(langRecommendTemplateMap['en-US'].replace(/^#mustache#/, '')),
  translatedContent: [],
};

export const recommendTemplates: Marketing.RecommendTemplate[] = [
  {
    multiLingualTemplates: [
      {
        lang: 'zh-CN',
        replyTemplate: langRecommendTemplateMap['zh-CN'],
        tag: '缺货时，推荐相似商品',
        description:
          '订单缺货时，告知买家订单中具体缺货商品，并且引导买家去改拍店铺其他商品（做相似商品推荐）',
      },
      {
        lang: 'en-US',
        replyTemplate: langRecommendTemplateMap['en-US'],
        tag: 'When out of stock, recommend similar products',
        description:
          'When the order is out of stock, Chatbot inform the buyer of the specific items in the order, and lead the buyer to buy other products in the store (recommend similar products)',
      },
      {
        lang: 'id',
        replyTemplate: langRecommendTemplateMap.id,
        tag: 'Saat stok kosong, berikan rekomendasi produk serupa',
        description:
          'Ketika ada stok produk yang habis pada pesanan buyer, informasikan kepada buyer bahwa produk terkait sedang kosong, arahkan untuk checkout produk lain di toko (buat rekomendasi produk serupa)',
      },
      {
        lang: 'th',
        replyTemplate: langRecommendTemplateMap.th,
        tag: 'เมื่อสินค้าหมด แนะนำสินค้าที่คล้ายกัน',
        description:
          'เมื่อสินค้าหมดสต็อก ให้แจ้งผู้ซื้อว่าสินค้าชิ้นไหนในคำสั่งซื้อหมดสต็อก และแนะนำให้ผู้ซื้อเปลี่ยนเป็นสินค้าอื่นในร้าน (แนะนำสินค้าที่คล้ายกันให้)',
      },
      {
        lang: 'ph',
        replyTemplate: langRecommendTemplateMap.ph,
        tag: 'Kung out of stock, mag-recommend ng similar products',
        description:
          'Kung ang order ay out of stock, Iinform ng Chatbot ang buyer ng mga specific items sa order, yayayaing ang buyer na bumili ng ibang mga products sa store (mag-recommend ng kaparehong products)',
      },
      {
        lang: 'es',
        replyTemplate: langRecommendTemplateMap.es,
        tag: 'Cuando se agote, recomiende productos similares',
        description:
          'Cuando el pedido está agotado, Chatbot informa al comprador de los artículos específicos en el pedido y lleva al comprador a comprar otros productos en la tienda (recomendar productos similares)',
      },
      {
        lang: 'vi',
        replyTemplate: langRecommendTemplateMap.vi,
        tag: 'Khi hết hàng, đề xuất sản phẩm tương tự',
        description:
          'Khi hết hàng, thông báo với người mua sản phẩm hết hàng cụ thể, đồng thời hướng dẫn người mua đặt sản phẩm khác (làm tương tự đề xuất sản phẩm)',
      },
      {
        lang: 'pt',
        replyTemplate: langRecommendTemplateMap.pt,
        tag: 'Quando estiver fora de estoque, recomende produtos similares',
        description:
          'Quando o pedido estiver fora de estoque, o Chatbot informará o comprador dos itens específicos no pedido e levará o comprador a comprar outros produtos na loja (recomendar produtos similares)',
      },
    ],
  },
  {
    multiLingualTemplates: [
      {
        lang: 'zh-CN',
        replyTemplate:
          '#mustache#{{#text}}亲，很抱歉，你的订单 {{/text}}{{#concept}}订单号{{/concept}}{{#text}} 中的 {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} 缺货了，由于平台的交货时间有限，可以麻烦您来关闭订单么？非常感谢您的理解～{{/text}}',
        tag: '缺货时，引导买家取消订单',
      },
      {
        lang: 'en-US',
        replyTemplate:
          '#mustache#{{#text}}Dear customer, we apologize that the item {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}}" in your order {{/text}}{{#concept}}订单号{{/concept}}{{#text}} is out of stock. As the platform\'s delivery time is limited, could you please kindly close the order? Thank you very much for your understanding~{{/text}}',
        tag: 'Guide buyers to cancel orders when out of stock',
      },
      {
        lang: 'id',
        replyTemplate:
          '#mustache#{{#text}}Maaf ya, kak. Barang {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} di pesanan {{/text}}{{#concept}}订单号{{/concept}}{{#text}} kamu sedang kosong. Karena waktu pengiriman di platform kami terbatas, bisakah kakak menutup pesanan tersebut? Terima kasih banyak atas pengertiannya~{{/text}}',
        tag: 'Mengarahkan pembeli untuk membatalkan pesanan ketika stok habis',
      },
      {
        lang: 'th',
        replyTemplate:
          '#mustache#{{#text}}ขอโทษนะคะ สินค้า {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} ในออเดอร์ {{/text}}{{#concept}}订单号{{/concept}}{{#text}}" ของคุณ หมดสต๊อกแล้วคะ เนื่องจากเวลาการจัดส่งในแพลตฟอร์มของเราจำกัด คุณสามารถช่วยปิดออเดอร์ได้ไหมคะ ขอบคุณมากๆ สำหรับความเข้าใจของคุณ~{{/text}}',
        tag: 'ช่วยเเนะนำลูกค้ายกเลิกคำสั่งซื้อเมื่อสินค้าหมด',
      },
      {
        lang: 'ph',
        replyTemplate:
          '#mustache#{{#text}}Dear customer, humihingi kami ng paumanhin dahil ang item na {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} sa iyong order na {{/text}}{{#concept}}订单号{{/concept}}{{#text}} ay wala sa stock. Dahil sa limitadong oras ng delivery ng platform, maaari mo bang pakiusap na isara ang order? Maraming salamat sa iyong pag-unawa~{{/text}}',
        tag: 'Gabayan ang mga mamimili na kanselahin ang mga order kapag wala sa stock',
      },
      {
        lang: 'es',
        replyTemplate:
          '#mustache#{{#text}}Dear customer, we apologize that the item {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}}" in your order {{/text}}{{#concept}}订单号{{/concept}}{{#text}} is out of stock. As the platform\'s delivery time is limited, could you please kindly close the order? Thank you very much for your understanding~{{/text}}',
        tag: 'Guide buyers to cancel orders when out of stock',
      },
      {
        lang: 'vi',
        replyTemplate:
          '#mustache#{{#text}}Xin lỗi quý khách, sản phẩm {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}} trong đơn hàng {{/text}}{{#concept}}订单号{{/concept}}{{#text}} của quý khách đã hết hàng. Vì thời gian giao hàng của nền tảng bị giới hạn, quý khách có thể vui lòng đóng đơn hàng được không ạ? Rất cảm ơn sự thông cảm của quý khách~{{/text}}',
        tag: 'Hướng dẫn người mua hủy đơn hàng khi hết hàng',
      },
      {
        lang: 'pt',
        replyTemplate:
          '#mustache#{{#text}}Dear customer, we apologize that the item {{/text}}{{#concept}}缺货商品名称{{/concept}}{{#text}}" in your order {{/text}}{{#concept}}订单号{{/concept}}{{#text}} is out of stock. As the platform\'s delivery time is limited, could you please kindly close the order? Thank you very much for your understanding~{{/text}}',
        tag: 'Guide buyers to cancel orders when out of stock',
      },
    ],
  },
];

export const OutStockTaskEmpty = (props: OutStockTaskEmptyProps) => {
  const { isTradeIds, scenarioType, platform, msgTypes, concepts, recommend } = props;
  const { t } = useTranslate();

  const demoValue = useMemo(() => {
    if (scenarioType === 'OUT_STOCK_REMINDER') {
      return holderData;
    }
    return {
      content: [],
      translatedContent: [],
    };
  }, [scenarioType]);

  const title = isTradeIds
    ? t('department.请输入x', { x: t('订单编号') })
    : scenarioType === 'ORDER_FOLLOWUP'
    ? t('请先上传跟进订单信息表')
    : t('aide.在您上传订单后更新');

  return (
    <div className="px-7 py-4 bg-fill-0 mb-4 flex relative" data-step="taskTip" title={title}>
      <div className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center ">
        {scenarioType === 'ORDER_FOLLOWUP' && (
          <p className="font-bold text-[var(--fett-surface-warning-text)] z-20">{title}</p>
        )}
        <div className="absolute top-0 right-0 bottom-0 left-0 bg-fill-0 opacity-30 z-10"></div>
      </div>
      <div className="flex-1 mr-6 border-t-0 border-b-0 border-l-0 border-r border-solid border-[#eee]">
        <div className="text-base mb-3">
          <LeadingLine />
          {t('aide.发送内容')}
        </div>
        <Form.Item>
          <EditorWrap className="cursor-not-allowed">
            <div className="pointer-events-none w-full">
              <ExecuteContextsFieldV2
                scenarioType={scenarioType}
                platform={platform}
                region="ID"
                msgTypes={msgTypes}
                value={demoValue}
                onChange={noop}
                error=""
                recommendTemplates={recommend}
              />
            </div>
          </EditorWrap>
        </Form.Item>
      </div>
      <ExtraTabs
        platform={platform}
        region="ID"
        concepts={concepts}
        allowDividing
        value={demoValue}
        recommendTemplates={recommend}
      />
    </div>
  );
};

export const LeadingLine = styled.b`
  border-left: #3370ff solid 4px;
  height: 14px;
  display: inline-block;
  font-size: 10px;
  margin-right: 8px;
`;

export const EditorWrap = styled.div`
  display: flex;
  [ui-editor] {
    flex: 1;
    margin-right: 24px;
  }
`;
